<template>
  <div>
    <div class="grid grid-cols-2 gap-x-6 gap-y-4 mt-5">
      <FormField id="firstName" label="Prénom" v-model="firstName" />
      <FormField id="lastName" label="Nom" v-model="lastName" />
      <FormField id="email" label="Email" v-model="email" />
      <FormField id="phone" label="Numéro de téléphone" v-model="phone" />
      <Selector label="Role" v-model="role" :options="roleOptions" />
      <Selector v-if="isRegionalManager" label="Région" v-model="region" :options="regionOptions" />
    </div>

    <div v-if="!isOrganizationAdmin" class="mt-7">
      <label class="block text-sm font-medium text-gray-700 mb-2">Points de vente</label>
      <CardSelector
        v-model="storeIds"
        :items="stores"
        label-key="salesChannel"
        value-key="id"
        placeholder="Rechercher un point de vente..."
        :max-selections="isSellerOrResponsible ? 1 : null"
      />
    </div>
    <div class="text-center mt-7 mb-5">
      <GenericButton
        @click="$emit('submit')"
        :disabled="(!isOrganizationAdmin && storeIds.length === 0) || (isRegionalManager && !region)"
      >
        Enregister
      </GenericButton>
    </div>
  </div>
</template>

<script>
import { Selector, FormField, GenericButton, CardSelector, toCamelCase } from "@estaly/ui";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    Selector,
    CardSelector,
    FormField,
    GenericButton,
  },
  props: {
    organizationId: {
      type: Number,
      required: false,
      default: null,
    },
    modelValue: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },
  emits: ["update:modelValue", "submit"],
  data() {
    return {
      firstName: this.modelValue?.firstName || null,
      lastName: this.modelValue?.lastName || null,
      email: this.modelValue?.email || null,
      role: this.modelValue?.role || null,
      phone: this.modelValue?.phone || null,
      region: this.modelValue?.region || null,
      storeIds: this.modelValue?.storeIds || [],
      regions: [],
      roleOptions: [
        {
          value: null,
          label: "Choisir un rôle",
        },
        {
          value: "seller",
          label: this.$t("roles.seller"),
        },
        {
          value: "responsible",
          label: this.$t("roles.responsible"),
        },
        {
          value: "claims_manager",
          label: this.$t("roles.claims_manager"),
        },
        {
          value: "customer_support_manager",
          label: this.$t("roles.customer_support_manager"),
        },
        {
          value: "accounting_manager",
          label: this.$t("roles.accounting_manager"),
        },
        {
          value: "manager",
          label: this.$t("roles.manager"),
        },
        {
          value: "regional_manager",
          label: this.$t("roles.regional_manager"),
        },
        {
          value: "store_admin",
          label: this.$t("roles.store_admin"),
        },
        {
          value: "organization_admin",
          label: this.$t("roles.organization_admin"),
        },
      ],
    };
  },
  computed: {
    ...mapGetters("stores", ["getIndex"]),
    isOrganizationAdmin() {
      return this.role === "organization_admin";
    },
    isSellerOrResponsible() {
      return this.role === "seller" || this.role === "responsible";
    },
    isRegionalManager() {
      return this.role === "regional_manager";
    },
    regionOptions() {
      return this.regions.map((r) => ({ value: r, label: r }));
    },
    stores() {
      return this.getIndex || [];
    },
  },
  async mounted() {
    await Promise.all([this.fetchStores(), this.fetchRegions()]);
    if (this.isRegionalManager && this.region) {
      await this.fetchStores(this.region);
    }
  },
  watch: {
    firstName(newValue) {
      this.$emit("update:modelValue", { ...this.modelValue, firstName: newValue });
    },
    lastName(newValue) {
      this.$emit("update:modelValue", { ...this.modelValue, lastName: newValue });
    },
    email(newValue) {
      this.$emit("update:modelValue", { ...this.modelValue, email: newValue });
    },
    phone(newValue) {
      this.$emit("update:modelValue", { ...this.modelValue, phone: newValue });
    },
    storeIds(newValue) {
      this.$emit("update:modelValue", { ...this.modelValue, storeIds: newValue });
    },
    role: {
      handler() {
        if (this.isOrganizationAdmin) {
          this.storeIds = [];
        } else if (this.isSellerOrResponsible && this.storeIds.length > 1) {
          this.storeIds = this.storeIds.slice(0, 1);
        }
        if (!this.isRegionalManager) {
          this.region = null;
        }
        this.$emit("update:modelValue", {
          ...this.modelValue,
          role: this.role,
          region: this.region,
          storeIds: this.storeIds,
        });
      },
      flush: "sync",
    },
    async region(newRegion) {
      if (!this.isRegionalManager) return;
      this.$emit("update:modelValue", { ...this.modelValue, region: newRegion });
      if (newRegion) {
        await this.fetchStoresByRegion(newRegion);
      }
    },
  },
  methods: {
    ...mapActions("stores", ["fetchIndex"]),
    ...mapActions("organizations", { fetchOrganization: "fetch" }),

    async fetchStores(region = null) {
      const searchParams = { status: ["active"], perPage: 2000 };
      if (this.organizationId) searchParams.organizationId = [this.organizationId];
      if (region) searchParams.region = [region];
      await this.fetchIndex(searchParams);
    },
    async fetchRegions() {
      if (!this.organizationId) return;
      const response = await this.fetchOrganization({ id: this.organizationId });
      if (!response?.success) return;
      const organization = toCamelCase(response.data);
      this.regions = organization.regions || [];
    },
    async fetchStoresByRegion(region) {
      this.storeIds = [];
      await this.fetchStores(region);
    },
  },
};
</script>
